// biome-ignore-all lint/performance/noBarrelFile: Package entrypoint intentionally re-exports Claude Code runtime helpers.
import { bridgeAgentControllerConversationRef, buildAdapterCancelRequest, buildExecutableLaunchContract, createSharedControlShell, createSharedDispatchShell, resolveBuiltinAdapterConversationBridgeSpec, } from "@wakeloop/adapter-core";
import { resolveClaudeCodeTimeoutMs as resolveClaudeCodeTimeoutMsLocal, verifyClaudeCodeLaunchContract as verifyClaudeCodeLaunchContractLocal, } from "./config/claude-runtime-config.js";
import { invokeClaudeWithSingleContextHeal as invokeClaudeWithSingleContextHealLocal } from "./invoke/claude-invoke.js";
export { resolveClaudeCodeTimeoutMs, resolveInstalledClaudeCodeLaunchCandidates, resolveInstalledClaudeCodeLaunchContract, verifyClaudeCodeLaunchContract, } from "./config/claude-runtime-config.js";
export { isLikelyClaudeContextLostError } from "./context/claude-context-heal.js";
export { ClaudeRuntimeError, invokeClaudeWithSingleContextHeal, } from "./invoke/claude-invoke.js";
export { parseClaudeOutputLine } from "./output/claude-output-parser.js";
export { createClaudeAgentSdkToolActivityTracker, } from "./output/claude-tool-activity.js";
export { ClaudeAgentSdkTransportError, invokeClaudeAgentSdkTransport, resolveClaudeAgentSdkExecutable, } from "./transport/claude-agent-sdk-transport.js";
export { ClaudeCliTransportError, invokeClaudeCliTransport, } from "./transport/claude-cli-transport.js";
const CLAUDE_CODE_CONVERSATION_BRIDGE_SPEC = resolveBuiltinAdapterConversationBridgeSpec("claude-code");
const EXPECTED_FORCE_CLOSE_INTERRUPT_ERROR_RE = /query closed before response received/i;
export const createClaudeCodeAdapter = (options) => {
    const activeAgentSdkQueries = new Map();
    const registerAgentSdkQuery = ({ query, taskId, }) => {
        activeAgentSdkQueries.set(taskId, query);
        return () => {
            if (activeAgentSdkQueries.get(taskId) === query) {
                activeAgentSdkQueries.delete(taskId);
            }
        };
    };
    const sendInterrupt = async (signal) => {
        const activeQuery = activeAgentSdkQueries.get(signal.taskId);
        if (activeQuery) {
            if (signal.mode === "force") {
                Promise.resolve()
                    .then(() => activeQuery.interrupt())
                    .catch((error) => {
                    if (isExpectedForceCloseInterruptError(error)) {
                        return;
                    }
                    console.warn("[wakeloop.claude_code.force_interrupt_failed_after_close]", {
                        error: error instanceof Error ? error.message : String(error),
                        taskId: signal.taskId,
                    });
                });
                activeQuery.close();
                return;
            }
            await activeQuery.interrupt();
            return;
        }
        if (options.sendInterrupt) {
            await options.sendInterrupt(signal);
            return;
        }
        throw Object.assign(new Error("Claude Code interrupt is not available for this task."), {
            code: "adapter.unsupported",
        });
    };
    const controlShell = createSharedControlShell({
        gracefulTimeoutMs: options.gracefulTimeoutMs,
        sendCancel: options.sendCancel,
        sendInterrupt,
        waitForMs: options.waitForMs,
    });
    const dispatch = createSharedDispatchShell({
        adapterId: "claude-code",
        bridgeSpec: CLAUDE_CODE_CONVERSATION_BRIDGE_SPEC,
        invoke: options.invoke,
        invokeByMode: options.invokeByMode,
        resolveDefaultInvoke: () => resolveDefaultClaudeCodeInvoke({
            options,
            registerAgentSdkQuery,
        }),
        runtimeOptions: options,
    });
    return {
        adapterId: "claude-code",
        cancel: controlShell.cancel,
        dispatch,
        interrupt: controlShell.interrupt,
    };
};
const isExpectedForceCloseInterruptError = (error) => {
    if (!(error instanceof Error)) {
        return false;
    }
    return EXPECTED_FORCE_CLOSE_INTERRUPT_ERROR_RE.test(error.message);
};
export const buildClaudeCodeInterruptRequest = (input) => ({
    mode: "graceful",
    reason: input.reason,
    taskId: input.taskId,
});
export const buildClaudeCodeCancelRequest = (input) => buildAdapterCancelRequest({
    reason: input.reason,
    taskId: input.taskId,
});
const resolveDefaultClaudeCodeInvoke = (input) => {
    const { options, registerAgentSdkQuery } = input;
    const claudeTimeoutMs = options.claudeTimeoutMs ?? resolveClaudeCodeTimeoutMsLocal();
    return async (dispatchInput) => {
        const launchContract = await resolveClaudeDispatchLaunchContract({
            claudeBin: options.claudeBin,
            inputLaunchContract: dispatchInput.launchContract,
            optionLaunchContract: options.launchContract,
        });
        const registerRuntimeProcess = options.registerRuntimeProcess;
        const onProcessControlEvent = registerRuntimeProcess
            ? (event) => {
                registerRuntimeProcess({
                    ...event,
                    taskId: dispatchInput.taskId,
                });
            }
            : undefined;
        const result = await invokeClaudeWithSingleContextHealLocal({
            agentControllerPermissionMode: dispatchInput.agentControllerPermissionMode,
            launchContract,
            claudeTimeoutMs,
            claudeTransport: options.claudeTransport,
            claudeWorkdir: dispatchInput.runtimeContext.providerRuntimeWorkingDirectory,
            conversationPolicy: dispatchInput.conversationPolicy,
            ...(options.createAgentSdkQuery
                ? { createAgentSdkQuery: options.createAgentSdkQuery }
                : {}),
            env: dispatchInput.env,
            executionMode: dispatchInput.executionMode,
            ...(onProcessControlEvent ? { onProcessControlEvent } : {}),
            ...(dispatchInput.onRuntimeEvent
                ? { onRuntimeEvent: dispatchInput.onRuntimeEvent }
                : {}),
            prompt: dispatchInput.prompt,
            registerAgentSdkQuery,
            sessionId: normalizeOptionalText(dispatchInput.agentControllerConversationId),
            ...(dispatchInput.onPreview
                ? { onPreview: dispatchInput.onPreview }
                : {}),
            streamingMode: dispatchInput.streamingMode,
            taskId: dispatchInput.taskId,
        });
        const agentControllerConversation = CLAUDE_CODE_CONVERSATION_BRIDGE_SPEC
            ? bridgeAgentControllerConversationRef({
                bridgeSpec: CLAUDE_CODE_CONVERSATION_BRIDGE_SPEC,
                agentControllerConversationId: result.sessionId,
            })
            : null;
        return {
            contextDelta: {
                agentControllerConversationId: result.sessionId,
                refreshed: result.contextHealed,
                ...(agentControllerConversation ? { agentControllerConversation } : {}),
            },
            output: result.output,
            ...(result.telemetry ? { telemetry: result.telemetry } : {}),
        };
    };
};
async function resolveClaudeDispatchLaunchContract(input) {
    const explicitLaunchContract = input.inputLaunchContract ??
        input.optionLaunchContract ??
        (normalizeOptionalText(input.claudeBin)
            ? buildExecutableLaunchContract({
                commandPath: normalizeOptionalText(input.claudeBin),
            })
            : null);
    if (explicitLaunchContract) {
        const verification = await verifyClaudeCodeLaunchContractLocal({
            launchContract: explicitLaunchContract,
        });
        if (verification.ok) {
            return explicitLaunchContract;
        }
        throw Object.assign(new Error(verification.errorMessage), {
            code: "controller.launch.needs_repair",
            errorType: "config",
        });
    }
    throw Object.assign(new Error("Claude Code launch contract is required for runtime dispatch."), {
        code: "controller.launch.needs_repair",
        errorType: "config",
    });
}
const normalizeOptionalText = (value) => {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 ? normalized : null;
};
